import {
  useQuery,
  useMutation,
  useQueryClient,
  type QueryClient,
} from "@tanstack/react-query";
import fpFetch from "@/services/fpFetch";
import { BASE_API_URL_V2 } from "@/constants";
import type {
  EntityKind,
  AggregationKind,
  ViewConfig,
  FrontendFilter,
} from "../Dashboard/types";
import useOrgId from "@/components/FpAppV4/hooks/useOrgId";
import {
  RMS_BOOTSTRAP_QUERY_KEY,
  rmsBootstrapQueryKey,
  setBootstrapViews,
  useRmsBootstrap,
  type RmsBootstrap,
} from "./bootstrap";
import {
  DEFAULT_STRIP_COLLECTION,
  joinStrip,
  stripGroups,
  stripOf,
  type StripCollection,
} from "./stripModel";
import { stripCacheKey, useCollectionStrip, withCollection } from "./strip";
// Every view the page reads is shown under its display name (#13370 round 36i).
import { shownView } from "./viewNames";
import { mergeSavedView } from "../ViewStrip/pinning";
import type { DashboardConfig, DashboardWriteConfig } from "../Dashboards/data";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface TableConfig {
  // Board projection layout (#11426 gaps-v6 item 1): the saved axis and
  // card travel with the view — the same rows grouped a different way, or
  // cards showing different fields, is a different question.
  boardAxis?: string | null;
  boardCard?: { subtitle: string | null; fields: string[] } | null;
  columns?: string[];
  columnWidths?: Record<string, number>;
  columnOrder?: string[];
  sortBy?: string; // legacy single-sort (read for backwards compat)
  sortOrder?: "asc" | "desc"; // legacy single-sort
  sorts?: Array<{ column: string; order: "asc" | "desc" }>;
  // Ordered group-by columns (#11072). Absent on views saved before grouping
  // was part of a view, which read as ungrouped.
  groupBy?: string[];
  searchText?: string;
  pageSize?: number;
}

// The discriminator on a view saved from an entity page's tab (#11072,
// #12023). Such a view belongs to a record TYPE — it is a chip on every
// record's tab of that kind — and never appears in the landing strip.
export const ENTITY_TAB_SURFACE = "entity_tab" as const;
// The organization's shared views on a tab (#12023): one strip per record
// type that every member sees, in one order, and only RMS managers change.
export const ENTITY_TAB_SHARED_PRESET = "shared" as const;

// A dashboard (.claude/features/12900-rms-dashboards.md) is a view whose
// `view_type` is "dashboard" (#12953): a saved layout of cards with its own
// filters. It sits in the strip, the groups and the palette like any view, and
// opens at /rms/dashboards/<public_id> rather than at a view slug.
export const DASHBOARD_VIEW_TYPE = "dashboard" as const;

/** Whether a view is a dashboard rather than a data view (a table or a board over records). */
export const isDashboardView = (view: Pick<RmsView, "view_type">): boolean =>
  view.view_type === DASHBOARD_VIEW_TYPE;

// The entity-tab fields a view config may carry, shared by the stored shape
// and both write payloads so the three cannot drift. The #11072 shape (one
// private auto-saving view per user, preset "tab") is no longer read or
// written: the backend offers those rows to nobody, so no surface here
// models them.
export interface EntityTabConfigFields {
  surface?: typeof ENTITY_TAB_SURFACE;
  // A dashboard's layout (a view of type "dashboard"). Always written whole:
  // the server merges config keys shallowly.
  dashboard?: DashboardConfig;
  preset?: typeof ENTITY_TAB_SHARED_PRESET;
  // The chip's place in the organization's strip (#12023). Org-wide, so
  // it lives on the row rather than in the per-user strip prefs.
  position?: number;
}

/**
 * A Documents & Interactions view's own List settings (#13370, round 35): what its List groups
 * by, its columns (the List's keys, Filename first) and the order of its Document Group
 * sections. Each absent keeps the record type's (the layout's, then the dashboard config's).
 */
export interface ViewListConfig {
  group_by?: readonly string[] | null;
  columns?: readonly string[] | null;
  group_order?: readonly string[] | null;
}

export interface SharedRef {
  id: string;
  type: "edit" | "view";
}

export interface RmsView {
  id: number;
  public_id: string;
  organization_id: string;
  created_by: string;
  // Resolved display name/email of the creator (backend-resolved from
  // created_by). Optional so an older backend that omits them reads as
  // undefined. Surfaced in the view Details modal.
  created_by_name?: string | null;
  created_by_email?: string | null;
  name: string;
  view_type: "search" | "aggregation" | typeof DASHBOARD_VIEW_TYPE;
  entity_kind: EntityKind | null; // null for aggregation views
  aggregation_kind: AggregationKind | null; // null for search views
  config: EntityTabConfigFields & {
    entity_kind?:
      | EntityKind
      | "performance"
      | "transactions"
      | "holdings"
      | "dataset"
      | "activity"
      | "tasks"
      | "document_review";
    // The Holdings view's as-of-date (YYYY-MM-DD), persisted so a saved view
    // reopens at the same point in time (#10835).
    as_of_date?: string;
    // The dataset a `dataset` view shows (#12178).
    dataset_id?: number;
    // A view the BCBSWY documents page seeds (#11422 R20.1): which default
    // it is, kept through a rename (`fp/clients/bcbswy/default_views.py`).
    default_view?: string;
    aggregation_kind?: AggregationKind;
    flat?: boolean;
    filters?: FrontendFilter[];
    table_config?: TableConfig | null;
    // A Documents & Interactions view's List settings (#13370, round 35).
    list_config?: ViewListConfig | null;
    // Map lens persistence (#9528). When `default_render === "map"` and
    // `map_config.address_attribute_id` is set, opening this view drops
    // the user straight into MapView (no URL param needed). Both fields
    // are optional and additive — existing views are unchanged.
    default_render?: "table" | "map";
    map_config?: {
      address_attribute_id: string;
    };
  };
  shared_with: { users: SharedRef[]; teams: SharedRef[] } | null;
  created_at: string;
  updated_at: string;
  current_user_permission: "edit" | "view";
  // Whether the signed-in user may delete this view, decided by the server's one rule
  // (.claude/features/13269-view-delete-rights.md). When false, `delete_blocker` is the server's
  // sentence naming who may delete it, shown as given: the page never restates the rule.
  // Optional so a backend that predates them (or one mid-deploy) reads as undefined, which the
  // menus treat as allowed: the server's 403 then carries the reason.
  can_delete?: boolean;
  delete_blocker?: string | null;
  is_org_wide: boolean;
  // Why the current user can see this view (#12050). `staff_override` means
  // the super-admin arm of the authorization ladder is the only reason: it is
  // another person's private view, and the strip never lists it. Optional so
  // an older backend that omits it reads as an ordinary view.
  visibility?: RmsViewVisibility;
  // Read from the user's strip (`joinStrip`, Data/strip.ts), never from the
  // server's view row: the strip is the one source of group placement and pin
  // state. Absent on a view that has not been through the join.
  group_id?: number | null;
  user_hidden?: boolean;
}

export type RmsViewVisibility =
  | "own"
  | "org_wide"
  | "shared"
  | "staff_override";

/** A view the current user reaches only as Finpilot staff (#12050). */
export const isStaffOverrideView = (view: RmsView): boolean =>
  view.visibility === "staff_override";

export const GROUP_COLORS = [
  "blue",
  "green",
  "red",
  "yellow",
  "purple",
  "orange",
] as const;
export type GroupColor = (typeof GROUP_COLORS)[number];

export interface RmsViewGroup {
  id: number;
  name: string;
  color: GroupColor;
  // An org group is the whole organization's; a personal one, its owner's.
  scope: "organization" | "personal";
  // The reader's level on the group (#13896); absent from an older server.
  permission?: "view" | "edit" | "admin";
}

/** Each item once, the first with its id kept: a view sits once in each group
 * that holds it (#13896), and some lists show views, not placements. */
export const uniqueById = <T extends { id: number }>(
  items: readonly T[]
): T[] => {
  const seen = new Set<number>();
  return items.filter((item) => {
    if (seen.has(item.id)) return false;
    seen.add(item.id);
    return true;
  });
};

export function getViewConfig(view: RmsView): ViewConfig {
  if (view.config.entity_kind === "performance") {
    return { type: "performance" };
  }
  if (view.config.entity_kind === "transactions") {
    return { type: "transactions" };
  }
  if (view.config.entity_kind === "holdings") {
    return { type: "holdings", asOfDate: view.config.as_of_date };
  }
  if (
    view.config.entity_kind === "dataset" &&
    view.config.dataset_id !== undefined
  ) {
    // The view is named after its dataset at creation, and a rename is the
    // user's own label for the same rows, so the view's name is the label.
    return {
      type: "dataset",
      datasetId: view.config.dataset_id,
      datasetName: view.name,
    };
  }
  if (view.config.entity_kind === "activity") {
    return { type: "activity" };
  }
  if (view.config.entity_kind === "tasks") {
    return { type: "tasks" };
  }
  if (view.config.entity_kind === "document_review") {
    return { type: "document_review" };
  }
  if (view.view_type === "aggregation") {
    return {
      type: "aggregation",
      panelKind: view.aggregation_kind!,
      flat: view.config.flat,
    };
  }
  return { type: "search", entityKind: view.entity_kind! };
}

export interface CreateViewPayload {
  name?: string;
  // Only a dashboard names its type; a data view's follows from its config.
  view_type?: typeof DASHBOARD_VIEW_TYPE;
  config?: EntityTabConfigFields & {
    // Null for a mixed dashboard ("Mixed records"), whose clauses each carry their kind (#12938).
    entity_kind?:
      | null
      | EntityKind
      | "performance"
      | "transactions"
      | "holdings"
      | "dataset"
      | "activity"
      | "tasks"
      | "document_review";
    // The Holdings view's as-of-date (YYYY-MM-DD), persisted so a saved view
    // reopens at the same point in time (#10835).
    as_of_date?: string;
    // The dataset a `dataset` view shows (#12178).
    dataset_id?: number;
    aggregation_kind?: AggregationKind;
    filters?: FrontendFilter[];
    table_config?: TableConfig;
  };
}

export interface UpdateViewPayload {
  name?: string;
  config?: EntityTabConfigFields & {
    entity_kind?:
      | EntityKind
      | "performance"
      | "transactions"
      | "holdings"
      | "dataset"
      | "activity"
      | "tasks"
      | "document_review";
    // The Holdings view's as-of-date (YYYY-MM-DD), persisted so a saved view
    // reopens at the same point in time (#10835).
    as_of_date?: string;
    // The dataset a `dataset` view shows (#12178).
    dataset_id?: number;
    aggregation_kind?: AggregationKind;
    filters?: FrontendFilter[];
    table_config?: TableConfig | null;
    list_config?: ViewListConfig | null;
    flat?: boolean;
    default_render?: "table" | "map";
    map_config?: { address_attribute_id: string } | undefined;
  };
  shared_with?: { users: SharedRef[]; teams: SharedRef[] } | null;
  updated_at: string;
}

/** A dashboard write that may carry a widget the server places (`withWidgetOnFirstTab`). */
export interface DashboardWritePayload
  extends Omit<UpdateViewPayload, "config"> {
  config: Omit<DashboardWriteConfig, "entity_kind"> & {
    entity_kind?: EntityKind;
  };
}

export interface OrderingItem {
  view_id: number;
  position: number;
}

// ---------------------------------------------------------------------------
// API functions
// ---------------------------------------------------------------------------

/**
 * A views request the server refused. `detail` is the sentence the server gave for it, when it
 * gave one: a caller with its own words for the failure shows those when the server said none.
 */
class ViewRequestError extends Error {
  constructor(
    message: string,
    readonly detail: string | null
  ) {
    super(message);
    this.name = "ViewRequestError";
  }
}

/** The error a failed views request reports: the server's message or detail, else what failed
 *  and the status. Shared with the dashboards' requests (Dashboards/data.ts). */
export const viewsResponseError = async (
  response: Response,
  what: string
): Promise<Error> => {
  const errorData: { message?: string; detail?: string } = await response
    .json()
    .catch(() => ({}));
  const detail = errorData.message || errorData.detail || null;
  return new ViewRequestError(
    detail ?? `Failed to ${what}: ${response.status}`,
    detail
  );
};

// The RMS's own view store. A collection with a store of its own (a process
// view's `viewsEndpoint`, #11422) passes its endpoint as `base`.
const RMS_VIEWS_ENDPOINT = "/api/v2/rms/views";

const viewsUrlFor = (base: string = RMS_VIEWS_ENDPOINT): string =>
  `${BASE_API_URL_V2}${base}`;

const fetchViews = async (
  options: {
    viewType?: string;
    surface?: typeof ENTITY_TAB_SURFACE;
  },
  base?: string
): Promise<RmsView[]> => {
  const viewsUrl = viewsUrlFor(base);
  const params = new URLSearchParams();
  if (options.viewType) params.set("view_type", options.viewType);
  if (options.surface) params.set("surface", options.surface);
  const qs = params.toString();
  const url = qs ? `${viewsUrl}?${qs}` : viewsUrl;

  const response = await fpFetch(url, {
    method: "GET",
    headers: { "Content-Type": "application/json" },
  });

  if (!response.ok) {
    throw await viewsResponseError(response, "fetch views");
  }

  return ((await response.json()) as RmsView[]).map(shownView);
};

const fetchView = async (viewId: number, base?: string): Promise<RmsView> => {
  const response = await fpFetch(`${viewsUrlFor(base)}/${viewId}`, {
    method: "GET",
    headers: { "Content-Type": "application/json" },
  });

  if (!response.ok) {
    throw await viewsResponseError(response, "fetch view");
  }

  return shownView((await response.json()) as RmsView);
};

export const createView = async (
  payload: CreateViewPayload,
  base?: string
): Promise<RmsView> => {
  const response = await fpFetch(viewsUrlFor(base), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    throw await viewsResponseError(response, "create view");
  }

  return shownView((await response.json()) as RmsView);
};

// What the user reads when a save is refused because the stored view changed
// since the page loaded it (#12151): what happened and the way out. It does
// not say who wrote, because the writer is often the same person (their own
// share-modal save staled the token). One string for the toast and the
// change bar's sentence, so they cannot drift apart. No "refresh": the hook
// puts the server's copy back in the cache on a refusal, so saving again
// carries a fresh token and overwrites.
export const VIEW_SAVE_CONFLICT_MESSAGE =
  "This view changed since you opened it. Save again to overwrite it.";

// The server's optimistic-concurrency refusal: the `updated_at` the write
// carried is no longer the row's. Thrown as a plain object rather than an
// Error so a caller can tell it from a failure it cannot retry.
export interface ViewConflictError {
  status: 409;
  message: string;
}

export const isViewSaveConflict = (
  error: unknown
): error is ViewConflictError =>
  typeof error === "object" &&
  error !== null &&
  (error as { status?: unknown }).status === 409;

export const updateView = async (
  viewId: number,
  payload: UpdateViewPayload | DashboardWritePayload,
  base?: string
): Promise<RmsView> => {
  const response = await fpFetch(`${viewsUrlFor(base)}/${viewId}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    if (response.status === 409) {
      const errorData = await response.json().catch(() => ({}));
      const conflict: ViewConflictError = {
        status: 409,
        message: errorData.error || "Conflict",
      };
      throw conflict;
    }
    throw await viewsResponseError(response, "update view");
  }

  return shownView((await response.json()) as RmsView);
};

// The server's refusal of a delete (403): its message is the one sentence saying who may delete
// the view (.claude/features/13269-view-delete-rights.md), shown to the person as it is.
export interface ViewDeleteRefusal {
  status: 403;
  message: string;
}

const isViewDeleteRefusal = (error: unknown): error is ViewDeleteRefusal =>
  typeof error === "object" &&
  error !== null &&
  (error as { status?: unknown }).status === 403 &&
  typeof (error as { message?: unknown }).message === "string";

export const deleteView = async (
  viewId: number,
  base?: string
): Promise<void> => {
  const response = await fpFetch(`${viewsUrlFor(base)}/${viewId}`, {
    method: "DELETE",
    headers: { "Content-Type": "application/json" },
  });

  if (!response.ok) {
    if (response.status === 403) {
      const errorData: { message?: string; detail?: string } = await response
        .json()
        .catch(() => ({}));
      const message = errorData.message || errorData.detail || "";
      if (message !== "") {
        const refusal: ViewDeleteRefusal = { status: 403, message };
        throw refusal;
      }
      throw new Error("Failed to delete view: 403");
    }
    throw await viewsResponseError(response, "delete view");
  }
};

/**
 * What the error toast says when a delete fails: the server's reason for a refusal (403), and a
 * plain retry for anything else (a network failure, a 5xx). The DELETE carries no lock token, so
 * it never answers 409.
 */
export const viewDeleteFailureMessage = (
  error: unknown,
  name: string
): string =>
  isViewDeleteRefusal(error)
    ? error.message
    : `${name} couldn't be deleted. Try again.`;

// With no surface, the caller's own strip order (per-user prefs); with the
// entity-tab surface, the organization's order of a tab's views (#12023).
export const updateViewOrdering = async (
  items: OrderingItem[],
  surface?: typeof ENTITY_TAB_SURFACE,
  base?: string
): Promise<{ success: boolean }> => {
  const viewsUrl = viewsUrlFor(base);
  const url = surface
    ? `${viewsUrl}/ordering?surface=${surface}`
    : `${viewsUrl}/ordering`;
  const response = await fpFetch(url, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ items }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      errorData.message || `Failed to update view ordering: ${response.status}`
    );
  }

  return response.json();
};

// ---------------------------------------------------------------------------
// View group API functions
// ---------------------------------------------------------------------------

const groupsUrl = `${BASE_API_URL_V2}/api/v2/rms/view-groups`;

// A group is made in one collection's strip and stays there (#11422 R6); its
// rename, colour and delete need none, since the group knows its own.
const createViewGroup = async (
  name: string = "New Group",
  collection: StripCollection = DEFAULT_STRIP_COLLECTION,
  scope?: RmsViewGroup["scope"]
): Promise<RmsViewGroup> => {
  const response = await fpFetch(withCollection(groupsUrl, collection), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    // No scope is the server's default, the maker's own group.
    body: JSON.stringify(scope === undefined ? { name } : { name, scope }),
  });
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      errorData.message || `Failed to create view group: ${response.status}`
    );
  }
  return response.json();
};

const renameViewGroup = async (
  groupId: number,
  name: string
): Promise<RmsViewGroup> => {
  const response = await fpFetch(`${groupsUrl}/${groupId}/rename`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ name }),
  });
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      errorData.message || `Failed to rename view group: ${response.status}`
    );
  }
  return response.json();
};

// Deletes the group, its memberships and its grants; never its views (#13896).
const deleteViewGroup = async (groupId: number): Promise<void> => {
  const response = await fpFetch(`${groupsUrl}/${groupId}`, {
    method: "DELETE",
    headers: { "Content-Type": "application/json" },
  });
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      errorData.message || `Failed to delete view group: ${response.status}`
    );
  }
};

const setGroupColor = async (
  groupId: number,
  color: GroupColor
): Promise<RmsViewGroup> => {
  const response = await fpFetch(`${groupsUrl}/${groupId}/color`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ color }),
  });
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      errorData.message || `Failed to set group color: ${response.status}`
    );
  }
  return response.json();
};

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

/** The viewer's default view, which bare /rms opens (#12953, #13052; Dashboards/data.ts). */
export const defaultViewQueryKey = (orgId: string | null | undefined) =>
  ["rms-default-view", orgId] as const;

export const RMS_VIEW_QUERY_KEYS = {
  all: ["rms-views"] as const,
  listPrefix: ["rms-views", "list"] as const,
  list: (viewType?: string) =>
    [...RMS_VIEW_QUERY_KEYS.all, "list", viewType] as const,
  detail: (viewId: number) =>
    [...RMS_VIEW_QUERY_KEYS.all, "detail", viewId] as const,
  // Keyed by organization: a staff org session swaps the acting org in place
  // with no remount, and the entity tabs must then speak that org's views.
  // A collection with its own view store (`base`) keeps its own list.
  entityTab: (organizationId: string, base?: string) =>
    base === undefined
      ? ([...RMS_VIEW_QUERY_KEYS.all, "entity-tab", organizationId] as const)
      : ([
          ...RMS_VIEW_QUERY_KEYS.all,
          "entity-tab",
          organizationId,
          base,
        ] as const),
};

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

// The pinned views, in strip order.
export const useRmsViews = (viewType?: string) => {
  return useRmsBootstrap<RmsView[]>({
    select: (data) => {
      const visible = joinStrip(data.views, stripOf(data))
        .filter((v) => !v.user_hidden)
        .map(shownView);
      return viewType
        ? visible.filter((v) => v.view_type === viewType)
        : visible;
    },
  });
};

// Every view in strip order, then the ones the strip does not hold.
export const useAllRmsViews = () => {
  return useRmsBootstrap<RmsView[]>({
    select: (data) => joinStrip(data.views, stripOf(data)).map(shownView),
  });
};

// The acting organization's shared entity-tab views (#12023): every record
// type's views in one list, fetched apart from the bootstrap
// so the landing strip's data and the tabs' data never mix. A null
// organization (the first paint before the identity payload lands) fetches
// nothing rather than asking for a shared fallback bucket.
export const useEntityTabViews = (
  organizationId: string | null,
  base?: string
) => {
  return useQuery({
    queryKey: RMS_VIEW_QUERY_KEYS.entityTab(organizationId ?? "", base),
    // A collection's own store holds only its views: it takes no surface.
    queryFn: () =>
      fetchViews(
        base === undefined ? { surface: ENTITY_TAB_SURFACE } : {},
        base
      ),
    enabled: organizationId !== null,
    staleTime: 30 * 1000,
  });
};

export const useCreateRmsView = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: CreateViewPayload) => createView(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: RMS_BOOTSTRAP_QUERY_KEY,
      });
    },
  });
};

// The cached copy of a view after a write to it: the server's row over what
// the list held (the response carries the new `updated_at`, which is the
// token the NEXT write must send), keeping the per-user and authorization
// columns a write response lacks (#11846, #12050).
const recordSavedView = (
  queryClient: QueryClient,
  orgId: string | undefined,
  saved: RmsView
): void => {
  setBootstrapViews(queryClient, orgId, (views) =>
    views.map((v) => (v.id === saved.id ? mergeSavedView(v, saved) : v))
  );
};

// After a write that bumps a view's `updated_at` but answers with something
// other than the row — the sharing endpoint answers with the sharing status —
// fetch the row and write it over the cached copy, so the next save carries
// the new token (#12151). The row is the authority for everything but the
// per-user pin state, which only the list carries.
export const refreshCachedView = async (
  queryClient: QueryClient,
  orgId: string | undefined,
  viewId: number
): Promise<void> => {
  let fresh: RmsView;
  try {
    fresh = await fetchView(viewId);
  } catch {
    // The row is gone or no longer visible: let the list say so.
    queryClient.invalidateQueries({ queryKey: RMS_BOOTSTRAP_QUERY_KEY });
    return;
  }
  setBootstrapViews(queryClient, orgId, (views) =>
    views.map((v) => (v.id === viewId ? { ...v, ...fresh } : v))
  );
  queryClient.invalidateQueries({ queryKey: RMS_BOOTSTRAP_QUERY_KEY });
};

const cachedView = (
  queryClient: QueryClient,
  orgId: string | undefined,
  viewId: number
): RmsView | undefined =>
  queryClient
    .getQueryData<RmsBootstrap>(rmsBootstrapQueryKey(orgId))
    ?.views.find((v) => v.id === viewId);

// A single writer for the view PUT, so every call site — the change bar's
// Save, the strip rename, the map-default toggle, the auto-save of default
// columns — gets the same bookkeeping (#12151):
//
//  - onMutate applies the write to the cached view at once, the way the
//    server will merge it (name over name, config keys over config keys), so
//    the surface reads as saved without waiting on the round trip;
//  - onSuccess writes the server's row over that, which is what keeps the
//    `updated_at` token fresh. Invalidating alone did not: every optimistic
//    bootstrap write cancels the refetch in flight (by design, #11426), so a
//    call site that wrote to the cache right after its mutation succeeded —
//    the strip rename did — left the OLD token in place, and the next Save
//    on that view was a 409;
//  - onError puts the cached view back as it was, then refetches, so a
//    rejected write never leaves the cache claiming it landed, and a 409
//    ends with the server's copy (and its token) in the cache for the retry.
export const useUpdateRmsView = () => {
  const queryClient = useQueryClient();
  const orgId = useOrgId();

  return useMutation({
    mutationFn: ({
      viewId,
      payload,
    }: {
      viewId: number;
      payload: UpdateViewPayload;
    }) => updateView(viewId, payload),
    onMutate: ({ viewId, payload }) => {
      const before = cachedView(queryClient, orgId, viewId);
      setBootstrapViews(queryClient, orgId, (views) =>
        views.map((v) =>
          v.id === viewId
            ? {
                ...v,
                ...(payload.name !== undefined ? { name: payload.name } : {}),
                ...(payload.config !== undefined
                  ? { config: { ...v.config, ...payload.config } }
                  : {}),
              }
            : v
        )
      );
      return { before };
    },
    onSuccess: (data, variables) => {
      queryClient.setQueryData(
        RMS_VIEW_QUERY_KEYS.detail(variables.viewId),
        data
      );
      recordSavedView(queryClient, orgId, data);
      queryClient.invalidateQueries({
        queryKey: RMS_BOOTSTRAP_QUERY_KEY,
      });
    },
    onError: (error, variables, context) => {
      const before = context?.before;
      if (before) {
        setBootstrapViews(queryClient, orgId, (views) =>
          views.map((v) => (v.id === variables.viewId ? before : v))
        );
      }
      queryClient.invalidateQueries({
        queryKey: RMS_BOOTSTRAP_QUERY_KEY,
      });
      // Every view write funnels through here — the Save buttons, the
      // renames, the map-default toggle — so a conflict is explained once,
      // for all of them. A red "Save failed" alone left the user with no
      // cause and no way out.
      if (isViewSaveConflict(error) && window.openFpToast) {
        window.openFpToast(VIEW_SAVE_CONFLICT_MESSAGE, "error");
      }
    },
  });
};

/**
 * Deletes a view and says what happened (.claude/features/13269-view-delete-rights.md): a success toast
 * when the server answers 204, an error toast with the reason when it does not. Nothing is
 * removed from the page before the server answers, so a failed delete leaves the tab or row
 * where it was.
 */
export const useDeleteRmsView = () => {
  const queryClient = useQueryClient();
  const orgId = useOrgId();

  return useMutation({
    mutationFn: ({ viewId }: { viewId: number; name: string }) =>
      deleteView(viewId),
    onError: (error, { name }) => {
      if (window.openFpToast) {
        window.openFpToast(viewDeleteFailureMessage(error, name), "error");
      }
    },
    onSuccess: (_, { viewId, name }) => {
      if (window.openFpToast) {
        window.openFpToast(`Deleted ${name}`, "success");
      }
      queryClient.removeQueries({
        queryKey: RMS_VIEW_QUERY_KEYS.detail(viewId),
      });
      // The deleted view may be the default bare /rms opens: read the default again, or /rms
      // keeps opening a view that is gone.
      queryClient.invalidateQueries({
        queryKey: defaultViewQueryKey(orgId),
      });
      queryClient.invalidateQueries({
        queryKey: RMS_BOOTSTRAP_QUERY_KEY,
      });
    },
  });
};

// ---------------------------------------------------------------------------
// View group hooks
// ---------------------------------------------------------------------------

// The groups of the RMS strip, which the bootstrap carries.
export const useRmsViewGroups = () => {
  return useRmsBootstrap<RmsViewGroup[]>({
    select: (data) => stripGroups(stripOf(data)),
  });
};

// The groups of another collection's strip (the documents page's, #11422
// R6), from that strip's own read.
export const useCollectionViewGroups = (
  collection: Exclude<StripCollection, "rms">
) => useCollectionStrip<RmsViewGroup[]>(collection, { select: stripGroups });

// `scope` is whose a new group is: absent, the maker's own (the RMS page);
// "organization" on the documents page, whose groups are the organization's
// (#11422 R6, B1), and which only a Write-role member can make.
export const useCreateViewGroup = (
  collection: StripCollection = DEFAULT_STRIP_COLLECTION,
  scope?: RmsViewGroup["scope"]
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (name?: string) => createViewGroup(name, collection, scope),
    // Settled, not only on success: the page changed the strip before the
    // request, and a refused change must not stay on screen.
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: stripCacheKey(collection),
      });
    },
  });
};

// The three below send no collection (the group knows its own); the one
// named says which strip to read again.
export const useRenameViewGroup = (
  collection: StripCollection = DEFAULT_STRIP_COLLECTION
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ groupId, name }: { groupId: number; name: string }) =>
      renameViewGroup(groupId, name),
    // Settled, not only on success: the page changed the strip before the
    // request, and a refused change must not stay on screen.
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: stripCacheKey(collection),
      });
    },
  });
};

export const useDeleteViewGroup = (
  collection: StripCollection = DEFAULT_STRIP_COLLECTION
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ groupId }: { groupId: number }) => deleteViewGroup(groupId),
    // Settled, not only on success: the page changed the strip before the
    // request, and a refused change must not stay on screen.
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: stripCacheKey(collection),
      });
    },
  });
};

export const useSetGroupColor = (
  collection: StripCollection = DEFAULT_STRIP_COLLECTION
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ groupId, color }: { groupId: number; color: GroupColor }) =>
      setGroupColor(groupId, color),
    // Settled, not only on success: the page changed the strip before the
    // request, and a refused change must not stay on screen.
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: stripCacheKey(collection),
      });
    },
  });
};
